// ════════════════════════════════════════════════════════════════════════════
//  ManDana — the record index
//  ---------------------------------------------------------------------------
//  One document naming every record on the account and the storage document it
//  lives in. Phase 2 reserved this (U-4) and left it unbuilt; the Librarian is
//  what needed it. It turns "what does this account contain" from a walk of
//  every home into a single read, and it is how an organizer enumerates records
//  without knowing where any of them are kept.
//
//  DERIVED, REBUILDABLE, NEVER AUTHORITATIVE. The index says what to ASK about;
//  mdRecordResolve answers. Nothing reads a title, a body or a date out of here,
//  so an entry that has gone stale cannot produce a wrong record — only a null,
//  which every caller already handles. That asymmetry is the whole safety
//  argument, and it runs one way: a record MISSING from the index is invisible
//  in a catalogue, and a catalogue that hides things is worse than none.
//
//  HOME MEANS ONE THING ON EVERY SHELF: the storage document where this record's
//  identity is kept, or `null` when its identity is in the code. Null is not
//  "unknown" — it is the honest answer for the ten domains and the built-in
//  Prayers project, and it is what lets the sync X-ray say "built into ManDana,
//  nothing to sync, nothing to lose" instead of guessing at a missing file.
//
//  FRESHNESS BY FINGERPRINT, NOT BY WRITE HOOKS. The obvious design is to have
//  every create and delete call in. It was rejected: nine shelves live in six
//  modules, and one forgotten call site makes a record permanently invisible —
//  exactly the failure above, arriving silently and staying. Instead the index
//  is a cache stamped with a hash of the storage documents it was built from,
//  checked on every read and rebuilt the moment it disagrees. A new shelf added
//  years from now is noticed without anybody remembering this file exists.
//
//  Which makes the ignore list below the one place staleness can enter, so it
//  is an EXCLUDE list and never an include list: a key forgotten here costs a
//  wasted rebuild, while a key wrongly listed here costs correctness. Nothing
//  belongs in it that a record's identity could ever be stored in.
//
//  THE COST, MEASURED. This once read "roughly 10–25 ms on a large account".
//  On the restored 5,000-record account it was nearer 150 ms, and it was paid
//  on every cell edit, because the fingerprint was computed twice per rebuild
//  and because edges and rosters — which cannot move a record's home — were
//  hashed like everything else. Both are fixed below: the hash is memoised
//  against mdStorageRevision(), and those four families are excluded. Hashing
//  content rather than lengths is what makes an edit-in-place detectable, and
//  it is still worth the bytes for the keys that can actually move a record.
//
//  LOCAL ONLY, AND HELD. The index is never offered to the server (mdLocalDocs):
//  it is a conclusion drawn from documents every device already has, so shipping
//  it would spend bandwidth to send an answer instead of its premises — and a
//  stale copy arriving from another device could hide records that are right
//  here. `record-index` is a held key so the deletion rule can never mourn a
//  document it was never sent, and md-sync's sanitiser refuses one that arrives.
// ════════════════════════════════════════════════════════════════════════════

(function (g: any) {
  'use strict';

  const KEY = 'md:record-index';
  const HELD = 'record-index';                     // sync-document form: no md: prefix
  const VERSION = 1;

  /* Keys the fingerprint ignores. Each is here because no record's identity can
     live in it AND it changes often enough to force pointless rebuilds. */
  const IGNORED = new Set<string>([
    KEY,                                           // the index cannot hash itself
    'md:syncMeta', 'md:syncRev', 'md:syncRevKey', 'md:syncTouched',  // sync bookkeeping, rewritten every tick
    'md:boardOpen', 'md:viewOffset', 'md:sidebarCollapsed',  // where the user is looking
    'md:theme', 'md:lang', 'md:calc',              // preferences and scratch
    /* The Omana roster: a cache of a table on the server, rewritten every time
       the database is opened, and holding nobody's records — the people in it
       are not this account's records at all. See app/md-omana.ts. */
    'md:omana',
  ]);

  /* The same exclusion, for the four families whose members are named rather
     than enumerable. They pass the header's test — nothing a record's identity
     could be stored in — because every one of them is what a DATABASE says
     about a record, and walk() below reads none of them: an edge holds a
     database's cells, a memberships roster holds which records a database has
     gathered, a proposal and a decision hold what the organizer suggested and
     what was answered. A record's home is its day map, its project records, the
     spine, its manifest, its board or its database row, and none of those four
     can move it.

     They are excluded because they are also, by far, the most written keys in
     the app: filling one cell in the Main DB writes an edge and a roster, and
     before this the whole index rebuilt in response — walking every day, every
     project, every board and every database to produce byte-for-byte what was
     already stored. On a 5,000-record account that was around 150 ms of dead
     work per keystroke-committed cell. */
  const IGNORED_PREFIXES = ['md:edge:', 'md:memberships:', 'md:proposal:', 'md:decision:'];
  const ignored = (key: string): boolean =>
    IGNORED.has(key) || IGNORED_PREFIXES.some((prefix) => key.startsWith(prefix));

  /* ── families where the KEY NAMES are the whole question ──────────────────
     walk() derives a database row's home from two ids and nothing else:

         homes[`dbrow:${row.id}`] = `md:database-row:${db.id}:${row.id}`

     Both are in the key. So rewriting a row — which is what committing a cell
     does — cannot move a single home, however many bytes changed. What CAN is a
     row appearing or disappearing, and that is a change to the key set, which
     mdStorageKeyRevision answers exactly.

     THIS IS A SECOND KIND OF EXCLUSION AND IT IS NOT AS SAFE AS THE FIRST. The
     ignore list above is safe because forgetting an entry costs a wasted
     rebuild. This one is not symmetric: a family listed here whose CONTENT can
     name a record would leave the index describing an account that has moved.
     So the rule is narrow, and it is a rule rather than a judgement — a family
     belongs here only if its homes are a pure function of its key names.

     `md:database:` deliberately does NOT qualify. A database document can still
     carry rows inline (mdDatabaseReadCanonical merges them out into their own
     documents on first read), so its bytes stay counted like everyone else's. */
  const KEYED_PREFIXES = ['md:database-row:'];

  /* ── C2: staleness is a question, not a reading of the account ────────────
     This was djb2 over every key the index was built from, paired with its raw
     bytes — 176,130 getItem calls for a handful of screens, around 150 ms every
     time somebody filled a cell, and the last whole-device read of any size in
     the app. It answered "did anything change?" by reproducing everything.

     The question is narrower than that: has anything been WRITTEN that could
     move a record's home? The chokepoint counts every write it accepts, per
     prefix (md-storage.js), so the answer is a subtraction — everything, minus
     the keys and families that provably cannot move one. Thirteen lookups, and
     no reading at all, on an account of any size.

     INCLUSIVE BY DEFAULT, unchanged, and that is a property being protected
     rather than a convenience. The ignore list stays the only exclusion, so a
     family nobody has thought of is still counted: it costs one wasted rebuild,
     which is the failure this module's own header says to prefer. Watching a
     named list of interesting families instead would be cheaper still, and
     would turn a family somebody forgot into an index that quietly describes an
     account that has moved — a wrong answer to "where does this record live?".

     THE EPOCH is not decoration. Counts cannot express "every document changed
     at once", which is what another tab restoring a backup looks like from
     here, and across that a subtraction could land on the very number this
     module was holding. The epoch moves exactly then, and cannot coincide.

     WHY IT IS STILL AN OPAQUE STRING. It is stored in the index document and
     compared against what a later boot computes. An index written by the old
     build simply fails to match and is rebuilt once — which is what should
     happen, and it needs no version bump and no migration. */
  function fingerprint(): string {
    if (typeof g.mdStorageWrites !== 'function') return legacyHash();
    let interesting = g.mdStorageWrites();
    for (const prefix of IGNORED_PREFIXES) interesting -= g.mdStorageWrites(prefix);
    for (const key of IGNORED) interesting -= g.mdStorageWrites(key);

    /* Subtracted like the ignored families, then added back as the one thing
       about them that matters. Without the key revision alongside, a row
       appearing and a row being edited would look identical from here — which
       is a stale index, not a slow one. */
    const keyed: string[] = [];
    for (const prefix of KEYED_PREFIXES) {
      interesting -= g.mdStorageWrites(prefix);
      keyed.push(String(typeof g.mdStorageKeyRevision === 'function' ? g.mdStorageKeyRevision(prefix) : 0));
    }

    const epoch = typeof g.mdStorageEpoch === 'function' ? g.mdStorageEpoch() : 0;
    return 'w1.' + epoch + '.' + interesting + '.' + keyed.join('.');
  }

  /** The original: djb2 over every key and every byte, with a NUL between the
      key and its value so no rename can imitate a rewrite. Kept for a host with
      no write counter — this module is loaded by tests and tools that bring up
      md-storage.js alone, and answering wrongly there would be worse than
      answering slowly.
      Keys are sorted so storage order cannot change the answer, and the count
      is prefixed so a rename that preserves total content still differs. */
  function legacyHash(): string {
    const keys: string[] = (g.mdStorageKeys ? g.mdStorageKeys() : [])
      .filter((key: string) => !ignored(key)).sort();
    let h = 5381;
    for (const key of keys) {
      const raw = g.mdReadRaw ? g.mdReadRaw(key) : null;
      const text = key + '\0' + (typeof raw === 'string' ? raw : '') + '\x01';
      for (let i = 0; i < text.length; i++) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
    }
    return keys.length + '.' + h.toString(36);
  }


  /* Every shelf, in the order mdRecordCandidates walks them — deliberately, so
     the two enumerations can be read side by side and any drift is visible.
     They are not the same list: this one includes database rows, which that one
     omits on purpose, because a catalogue lists what exists while a picker
     offers what you may link to. */
  function walk(): Record<string, string | null> {
    const homes: Record<string, string | null> = {};
    const seen = new Set<string>();

    /* Day records. A uid can appear in both day maps — a scheduled record that
       was also written all-day — and it is one record either way, so the first
       home found wins and events are asked first, matching the resolver. */
    const addDayMap = (kind: 'events' | 'records') => {
      const map = g.mdStoreAll ? g.mdStoreAll(kind) : null;
      for (const day of Object.keys(map || {}).sort()) {
        const list = map[day];
        if (!Array.isArray(list)) continue;
        for (const rec of list) {
          if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
          seen.add(rec.uid);
          homes[`${g.mdRecordType(rec)}:${day}:${rec.uid}`] = `md:${kind}:${day}`;
        }
      }
    };
    addDayMap('events');
    addDayMap('records');

    for (const rec of (g.mdProjectRecordIndex ? g.mdProjectRecordIndex() : [])) {
      if (!rec || typeof rec.uid !== 'string' || seen.has(rec.uid)) continue;
      seen.add(rec.uid);
      homes[`${g.mdRecordType(rec)}:${rec.uid}`] = 'md:projectRecords';
    }

    /* The spine. A built-in is materialised at index time and never stored, so
       it has no home — and saying so is more useful than leaving it out. */
    for (const pursuit of (g.mdPursuitIndex ? g.mdPursuitIndex() : []))
      homes[`pursuit:${pursuit.id}`] = pursuit.builtin ? null : 'md:pursuits';
    for (const project of (g.mdProjectIndex ? g.mdProjectIndex() : []))
      homes[`project:${project.id}`] = project.builtin ? null : 'md:projects';
    for (const domain of (g.MD_DOMAINS || []))
      homes[`domain:${domain.id}`] = null;

    for (const craft of (g.mdCraftList ? g.mdCraftList() : (g.mdManifestList ? g.mdManifestList() : [])))
      homes[`craft:${craft.instance}`] = 'md:crafts';
    for (const board of (g.mdBoardIndex ? g.mdBoardIndex() : []))
      homes[`board:${board.id}`] = 'md:boards';

    /* A database is one document and each of its rows is another, which is why
       a row's home names both ids while its ref names only its own. A database
       the app materialises — the Main DB, until the user changes something about
       it — has no document yet, and gets the same null a built-in pursuit gets:
       "ManDana describes this one" rather than a home nobody would find. */
    const stored = new Set<string>(g.mdStorageKeys ? g.mdStorageKeys() : []);
    for (const db of (g.mdDatabaseList ? g.mdDatabaseList() : [])) {
      const home = `md:database:${db.id}`;
      homes[`database:${db.id}`] = stored.has(home) ? home : null;
      for (const row of (Array.isArray(db.rows) ? db.rows : []))
        homes[`dbrow:${row.id}`] = `md:database-row:${db.id}:${row.id}`;
    }
    return homes;
  }

  /** Walk every home, write the result, and hand it back. The fingerprint is
      taken AFTER the walk because a canonical database read may migrate rows
      into their own documents as it goes — stamping first would record a state
      the walk itself had already left behind. */
  function mdRecordIndexRebuild(): Record<string, string | null> {
    const homes = walk();
    /* A walk that agrees with the document on disk has nothing to save. The
       stored index IS its homes; a fresher stamp and a fresher timestamp are
       bookkeeping, and buying them costs 239 KB at the quota wall, a document
       at the sync layer, and — because the write moves mdStorageRevision() —
       every derived cache in the app, including the Librarian's composition of
       all 5,000 records. Switching a view in the Main DB tripped exactly this
       and paid 230 ms to be told nothing had changed.

       The trade: a skipped write leaves the stamp stale, so the next read at a
       new revision walks the homes rather than trusting the hash. That walk is
       59 ms on that account, against 59 ms plus the 230 ms it replaces. The
       agreement is remembered below so it is paid once per revision, not once
       per read. */
    const doc = stored();
    if (!doc || !sameHomes(doc.homes, homes)) {
      g.mdWrite(KEY, { v: VERSION, at: Date.now(), fp: fingerprint(), homes });
    }
    remember(homes);
    return homes;
  }

  /** Whether two walks name the same records in the same places. Compared key
      by key rather than by JSON, because the two are built in different orders
      and a string compare would report every rebuild as a change — which is
      the bug this exists to end, wearing a different hat. */
  function sameHomes(a: any, b: Record<string, string | null>): boolean {
    if (!a || typeof a !== 'object') return false;
    const keys = Object.keys(b);
    if (Object.keys(a).length !== keys.length) return false;
    for (const key of keys) {
      if (!Object.prototype.hasOwnProperty.call(a, key) || a[key] !== b[key]) return false;
    }
    return true;
  }

  /* What the homes were, and the two answers that let a later read skip work.
     `revision` is the cheap one: nothing at all has been written, so nothing
     can have moved. `fp` is the one that earns its keep — a cell edit moves the
     revision but not the fingerprint, and without it every write sent the next
     read back to storage to re-read and re-parse the whole stored index. That
     is not a walk, but on the 5,110-key account it was still 0.7 ms of parsing
     per read, paid after every keystroke that commits. */
  let known: { revision: number; fp: string; homes: Record<string, string | null> } | null = null;
  function remember(homes: Record<string, string | null>): void {
    if (typeof g.mdStorageRevision !== 'function') return;
    known = { revision: g.mdStorageRevision(), fp: fingerprint(), homes };
  }

  /** The stored document, or null if there isn't one we recognise. A shape we
      cannot read is treated as no index at all rather than repaired: it costs
      one rebuild, and guessing at half a catalogue is not worth the saving. */
  function stored(): any | null {
    const doc = g.mdRead(KEY, null);
    if (!doc || typeof doc !== 'object' || Array.isArray(doc)) return null;
    if (doc.v !== VERSION) return null;
    if (!doc.homes || typeof doc.homes !== 'object' || Array.isArray(doc.homes)) return null;
    return doc;
  }

  /** ref key → home, for every record on the account.

      Verified by default: the fingerprint is checked and the index rebuilt if
      storage has moved since it was written. `{ verify: false }` hands back the
      document as it stands, which is what the X-ray needs to show what the last
      build believed — never what a caller resolving records should ask for. */
  function mdRecordIndexRead(options?: unknown): Record<string, string | null> {
    const opts = (options && typeof options === 'object' ? options : {}) as { verify?: boolean };
    if (opts.verify === false) {
      const asStored = stored();
      return asStored ? asStored.homes : {};
    }
    /* Verified, and already verified at this exact revision — see remember(). */
    if (known && typeof g.mdStorageRevision === 'function'
      && known.revision === g.mdStorageRevision()) return known.homes;
    /* Something was written, but nothing that can move a home. The copy in hand
       is still the answer, and reading the stored one back would only prove it. */
    if (known && known.fp === fingerprint()) { known.revision = g.mdStorageRevision(); return known.homes; }
    const doc = stored();
    if (doc && doc.fp === fingerprint()) { remember(doc.homes); return doc.homes; }
    return mdRecordIndexRebuild();
  }

  /** Where one record is kept: a storage key, `null` for a built-in, and
      `undefined` when the account holds no such record. Three answers, because
      "built into ManDana" and "not here" are not the same news. */
  function mdRecordIndexHome(refKey: unknown): string | null | undefined {
    if (typeof refKey !== 'string') return undefined;
    const homes = mdRecordIndexRead();
    return Object.prototype.hasOwnProperty.call(homes, refKey) ? homes[refKey] : undefined;
  }

  /** Every record on the account, as ref keys. */
  function mdRecordIndexKeys(): string[] {
    return Object.keys(mdRecordIndexRead());
  }

  /** Held, per the header: a device that has not built its index yet must not
      be able to tell every other device to delete theirs. */
  function mdRecordIndexHeldKeys(): string[] {
    return [HELD];
  }

  Object.assign(g, {
    MD_RECORD_INDEX_KEY: KEY,
    mdRecordIndexRebuild, mdRecordIndexRead, mdRecordIndexHome,
    mdRecordIndexKeys, mdRecordIndexHeldKeys,
  });

  // CommonJS escape hatch, following md-record.ts and md-resolve.ts.
  const mod = (globalThis as { module?: { exports?: unknown } }).module;
  if (mod && mod.exports) {
    mod.exports = { mdRecordIndexRebuild, mdRecordIndexRead, mdRecordIndexHome,
      mdRecordIndexKeys, mdRecordIndexHeldKeys };
  }
})(typeof window !== 'undefined' ? window : globalThis);
